import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { startOfDay, isBefore } from "date-fns";
import { useNotification } from "../../../../context/NotificationContext";
import { renderDate } from "../../utils/dateFormatter";

// ✅ Status color mapping
const statusColors = {
  reserved: "#3498db",      // Blue
  checked_in: "#27ae60",    // Green
  checked_out: "#8e44ad",   // Purple
  cancelled: "#e74c3c",     // Red
  noshow: "#f39c12",        // Orange
};

// Utility function to convert date to UTC midnight
const toUTCDate = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    const fallbackDate = new Date();
    return new Date(
      Date.UTC(fallbackDate.getFullYear(), fallbackDate.getMonth(), fallbackDate.getDate(), 0, 0, 0)
    );
  }
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
};

const UpdateDatesPopup = ({ bookingId, onSubmit, bookingData }) => {
  const [formValues, setFormValues] = useState({
    newCheckInDate: null,
    newCheckoutDate: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [nights, setNights] = useState(1);
  const submitButtonRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const businessDate = localStorage.getItem("business_date")
    ? startOfDay(new Date(localStorage.getItem("business_date")))
    : startOfDay(new Date());

  const { notify } = useNotification();

  useEffect(() => {
    if (bookingData) {
      try {
        const checkInDate = startOfDay(new Date(bookingData.checkIn || ""));
        const checkOutDate = startOfDay(new Date(bookingData.checkOut || ""));
        if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
          throw new Error("Invalid date format");
        }
        setFormValues({
          newCheckInDate: checkInDate,
          newCheckoutDate: checkOutDate,
        });
        setNights(calculateNights(checkInDate, checkOutDate));
      } catch (error) {
        console.error("Error parsing dates:", error);
        setFormValues({ newCheckInDate: null, newCheckoutDate: null });
        setErrors((prev) => ({
          ...prev,
          newCheckInDate: "Invalid check-in date format",
          newCheckoutDate: "Invalid checkout date format",
        }));
      }
    }
  }, [bookingData]);

  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut || isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) return 0;
    const checkInStart = startOfDay(checkIn);
    const checkOutStart = startOfDay(checkOut);
    const diffTime = Math.abs(checkOutStart - checkInStart);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };

  const handleDateChange = (field, date) => {
    if (!date || isNaN(date.getTime())) {
      setFormValues((prev) => ({ ...prev, [field]: null }));
      setErrors((prev) => ({ ...prev, [field]: "Please select a valid date" }));
      return;
    }
    const updatedDate = startOfDay(date);
    const updatedValues = { ...formValues, [field]: updatedDate };
    setFormValues(updatedValues);
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "newCheckInDate" || field === "newCheckoutDate") {
      setNights(calculateNights(updatedValues.newCheckInDate, updatedValues.newCheckoutDate));
    }
  };

  const validateForm = () => {
    let validationErrors = {};
    if (!formValues.newCheckInDate || isNaN(formValues.newCheckInDate.getTime())) {
      validationErrors.newCheckInDate = "Check-in date is required";
    }
    if (!formValues.newCheckoutDate || isNaN(formValues.newCheckoutDate.getTime())) {
      validationErrors.newCheckoutDate = "Checkout date is required";
    }

    if (formValues.newCheckInDate && formValues.newCheckoutDate) {
      const newCheckIn = startOfDay(formValues.newCheckInDate);
      const newCheckout = startOfDay(formValues.newCheckoutDate);
      const normalizedBusinessDate = startOfDay(businessDate);

      if (isBefore(newCheckout, newCheckIn)) {
        validationErrors.newCheckoutDate = "Checkout date must be on or after check-in date";
      }

      if (bookingData?.bookingStatus !== "checked_in") {
        if (isBefore(newCheckIn, normalizedBusinessDate) || isBefore(newCheckout, normalizedBusinessDate)) {
          validationErrors.newCheckInDate = "Dates cannot be before the business date";
        }
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("error", "Please fix the validation errors");
      return;
    }
    try {
      setLoading(true);
      const utcCheckIn = startOfDay(formValues.newCheckInDate);
      const utcCheckOut = startOfDay(formValues.newCheckoutDate);
      const payload = {
        bookingId,
        check_in_date: utcCheckIn.toISOString(),
        check_out_date: utcCheckOut.toISOString(),
      };
      const response = await axios.put(`${apiUrl}/booking/change-booking-dates`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify("success", "Dates updated successfully!");
      if (onSubmit) {
      onSubmit({
        ...formValues,
        guest_id: bookingData?.guest_id,
        bookingId,
      });
    }

      if (submitButtonRef.current) submitButtonRef.current.click();
    } catch (error) {
      let errorMessage = "Failed to update dates. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notify("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const isCheckedIn = bookingData?.bookingStatus === "checked_in";
  const isCheckedOutOrCancelledOrNoShow = ["checked_out", "cancelled", "noshow"].includes(
    bookingData?.bookingStatus
  );
  const disableCheckInDate = isCheckedIn || isCheckedOutOrCancelledOrNoShow;
  const disableCheckoutDate = isCheckedOutOrCancelledOrNoShow;

  const nextBooking =
    bookingData?.nextBookings?.find((b) => b.roomNumber === bookingData.roomNumber) || null;

  const statusColor = statusColors[bookingData?.bookingStatus] || "#7f8c8d";

  return (
    <div id="update_checkout" className="modal custom-modal fade" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            <div className="modal-header" style={{ backgroundColor: "#f0f8ff", borderBottom: "1px solid #e0e7ff" }}>
              <h5 className="modal-title" style={{ fontWeight: "600", color: "#333" }}>
                Update Check-in/Checkout Dates
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleCancel} />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
              <div className="row g-2">
                {/* Guest Details */}
<div className="col-12 mb-2">
  <div
    className="card"
    style={{
      border: "1px solid #e0e7ff",
      borderRadius: "6px",
      padding: "12px",
      backgroundColor: "#f9f9fb",
    }}
  >
    <div className="row g-0">
      <div className="col-6 pe-2">
        <p style={{ margin: "0.25rem 0", fontSize: "14px", fontWeight: "500", color: "#333" }}>
          <strong>Folio No:</strong> {bookingData?.bookingNumber || "N/A"}
        </p>
        <hr style={{ margin: "0.4rem 0" }} />
        <p style={{ margin: "0.25rem 0" }}>
          <strong>Status:</strong>{" "}
          <span
            style={{
              backgroundColor: statusColor,
              color: "#fff",
              padding: "6px 12px",
              borderRadius: "5px",
              fontSize: "13px",
              fontWeight: "600",
              display: "inline-block",
              minWidth: "90px",
              textAlign: "center",
              textTransform: "capitalize",
            }}
          >
            {bookingData?.bookingStatus || "N/A"}
          </span>
        </p>
        <hr style={{ margin: "0.4rem 0" }} />
        <p><strong>Arrival Date:</strong> {bookingData?.checkIn ? renderDate(bookingData.checkIn) : "N/A"}</p>
        <hr style={{ margin: "0.4rem 0" }} />
        <p><strong>Departure Date:</strong> {bookingData?.checkOut ? renderDate(bookingData.checkOut) : "N/A"}</p>
      </div>
      <div className="col-6 ps-2">
        <p><strong>Booking Date:</strong> {bookingData?.reservationDate ? renderDate(bookingData.reservationDate) : "N/A"}</p>
        <hr style={{ margin: "0.4rem 0" }} />
        <p><strong>Room Number:</strong> {bookingData?.roomNumber || "N/A"}</p>
        <hr style={{ margin: "0.4rem 0" }} />
        <p><strong>Pax:</strong> {`${bookingData?.guests?.adults || 0} Adults, ${bookingData?.guests?.children || 0} Children`}</p>
        <hr style={{ margin: "0.4rem 0" }} />
        <p
          style={{
            backgroundColor: "#4a4a4a",
            color: "#fff",
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: "4px",
          }}
        >
          <strong>Nights:</strong> {nights}
        </p>
      </div>
    </div>
  </div>
</div>

                {/* Next Booking */}
                {nextBooking && (
                  <div className="col-12 mb-2">
                    <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px", backgroundColor: "#fff3e6" }}>
                      <h6 className="card-title mb-1">Next Booking for Room {nextBooking.roomNumber}</h6>
                      <p><strong>Arrival:</strong> {renderDate(nextBooking.checkIn)}</p>
                      <p><strong>Departure:</strong> {renderDate(nextBooking.checkOut)}</p>
                    </div>
                  </div>
                )}

                {/* Date Selection */}
{/* Check-in / Check-out Section */}
<div className="col-12">
  <div
    className="card"
    style={{
      border: "1px solid #e0e7ff",
      borderRadius: "6px",
      padding: "15px",
      backgroundColor: "#fdfefe",
    }}
  >
    <h6 style={{ fontSize: "15px", fontWeight: "600", color: "#2c3e50", marginBottom: "12px" }}>
      Update Stay Dates
    </h6>
    <div className="row g-2 align-items-center">
      <div className="col-md-6">
        <label className="form-label">New Check-in Date</label>
        {bookingData?.bookingStatus === "checked_in" ? (
          <div className="readonly-date">
            {formValues.newCheckInDate ? renderDate(formValues.newCheckInDate) : "N/A"}
          </div>
        ) : (
          <DatePicker
            selected={formValues.newCheckInDate}
            onChange={(date) => handleDateChange("newCheckInDate", date)}
            minDate={businessDate}
            dateFormat="dd/MM/yyyy"
            className={`form-control ${errors.newCheckInDate ? "is-invalid" : ""}`}
            disabled={disableCheckInDate}
          />
        )}
        {errors.newCheckInDate && (
          <div className="invalid-feedback d-block">{errors.newCheckInDate}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">New Check-out Date</label>
        <DatePicker
          selected={formValues.newCheckoutDate}
          onChange={(date) => handleDateChange("newCheckoutDate", date)}
          minDate={formValues.newCheckInDate || businessDate}
          dateFormat="dd/MM/yyyy"
          className={`form-control ${errors.newCheckoutDate ? "is-invalid" : ""}`}
          disabled={disableCheckoutDate}
        />
        {errors.newCheckoutDate && (
          <div className="invalid-feedback d-block">{errors.newCheckoutDate}</div>
        )}
      </div>
    </div>
  </div>
</div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ backgroundColor: "#f0f8ff" }}>
              <button type="submit" className="btn btn-primary" disabled={loading || isCheckedOutOrCancelledOrNoShow}>
                {loading ? "Saving..." : "Save"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={isCheckedOutOrCancelledOrNoShow}>
                Cancel
              </button>
              <div style={{ display: "none" }}>
                <button ref={submitButtonRef} data-bs-dismiss="modal" type="reset">Submit2</button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateDatesPopup;
